import type { FormGroup } from '@angular/forms';
import type { ReactiveFormBuilderTourAdapter } from '../../adapters/reactive-form-builder.adapters';
import type { Form, FormField } from '../../interfaces/form.interface';
import type { ReactiveFieldRuntimeState } from '../../interfaces/reactive-field-runtime-state.interface';
import { ReactiveFieldRuntimeStateManager } from '../../model/reactive-field-runtime-state.manager';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';

export interface ReactiveFormViewStateFacadeConfiguration<T> {
  readonly getForm: () => Form<T> | null;
  readonly getModel: () => FormGroup | null;
  readonly buildFieldRuntimeStates: () => ReadonlyMap<string, ReactiveFieldRuntimeState<T>>;
  readonly hasLoadingDropdowns: (fieldStates: ReadonlyMap<string, ReactiveFieldRuntimeState<T>>) => boolean;
  readonly areExternalDropdownsReady: () => boolean;
  readonly hasPendingPreviousData: () => boolean;
  readonly needsLegacyDropdowns: () => boolean;
  readonly isDestroyed: () => boolean;
  readonly areTourAnchorsEnabled: () => boolean;
  readonly farmerTourService: ReactiveFormBuilderTourAdapter;
  readonly markForCheck: () => void;
  readonly detectChanges: () => void;
}

/**
 * Owns visual/runtime state used by the form shell and template.
 *
 * This keeps loading flags, render refresh counters, guidance tips and field readiness outside the Angular component.
 */
export class ReactiveFormViewStateFacade<T> {
  public fieldStates: ReadonlyMap<string, ReactiveFieldRuntimeState<T>> = new Map();
  public fieldRenderVersion = 0;
  public isLoading = false;
  public showFieldHelpButtons = false;

  constructor(private readonly configuration: ReactiveFormViewStateFacadeConfiguration<T>) {}

  public toggleFieldHelpButtons(): void {
    this.showFieldHelpButtons = !this.showFieldHelpButtons;
    this.configuration.markForCheck();
  }

  public startCurrentFormTour(): void {
    this.configuration.farmerTourService.startCurrentFormTour();
  }

  public getTourAnchorId(anchorId: string): string {
    return this.configuration.areTourAnchorsEnabled() ? anchorId : '';
  }

  public hasMainTableData(): boolean {
    const rows = this.configuration.getForm()?.table?.data;

    return Array.isArray(rows) && rows.length > 0;
  }

  public hasVisibleFields(): boolean {
    const sections = this.configuration.getForm()?.sections ?? [];

    return sections.some((section) => section.isVisible && section.fields.some((field) => field.conditions.isVisible));
  }

  public syncFieldRuntimeStates(): void {
    this.fieldStates = this.configuration.buildFieldRuntimeStates();
  }

  public updateLoadingState(): void {
    const hasRuntimeStates = this.fieldStates.size > 0;
    const hasLoadingField = hasRuntimeStates && this.configuration.hasLoadingDropdowns(this.fieldStates);
    const waitingLegacyDropdowns = this.configuration.needsLegacyDropdowns() && !hasRuntimeStates &&
      !this.configuration.areExternalDropdownsReady();

    this.isLoading = this.configuration.hasPendingPreviousData() || hasLoadingField || waitingLegacyDropdowns;
  }

  public refreshRenderedFields(): void {
    this.fieldRenderVersion += 1;

    if (this.configuration.isDestroyed()) {
      return;
    }

    this.configuration.detectChanges();
  }

  public isFieldReady(field: FormField<T>): boolean {
    const form = this.configuration.getForm();

    return ReactiveFieldRuntimeStateManager.isFieldReady(
      field,
      this.fieldStates,
      form?.type ?? 0,
      this.configuration.areExternalDropdownsReady(),
    );
  }

  public getPendingDisplay(field: FormField<T>): string {
    const name = field.name ?? '';

    if (!name) {
      return '';
    }

    const control = this.configuration.getModel()?.get(name);

    if (!control) {
      return '';
    }

    return ReactiveFormValueUtils.formatPreviewValue(control.value, field);
  }

  public getFormGuidanceTips(): readonly string[] {
    if (this.matchesFormContext(['fincas', 'finca', 'explotación', 'explotacion'])) {
      return [
        'Comprueba el titular o representante de la explotación.',
        'Revisa el código REA o los datos de registro si aparecen.',
        'Confirma teléfono, correo y dirección de contacto antes de guardar.',
      ];
    }

    if (this.matchesFormContext(['parcela', 'parcelas', 'recinto'])) {
      return [
        'Revisa que la finca, el recinto, el cultivo y la superficie coincidan con tu explotación.',
        'Si la parcela viene de REA o SIGPAC, no cambies datos oficiales sin comprobarlos antes.',
        'Usa los sectores si quieres agrupar parcelas que se trabajan igual.',
      ];
    }

    if (this.matchesFormContext(['maquina', 'maquinaria'])) {
      return [
        'Identifica la máquina con un nombre claro: marca, modelo o uso principal.',
        'Revisa ROMA, capacidad, propietario y fechas de inspección o mantenimiento si aparecen.',
        'Adjunta certificados, facturas o informes ITEAF cuando los tengas disponibles.',
      ];
    }

    if (this.matchesFormContext(['fitosanitario', 'tratamiento', 'tratamientos'])) {
      return [
        'Prepara fecha, parcela, producto, dosis y problema tratado.',
        'Comprueba el aplicador y, si corresponde, el número ROPO o la máquina utilizada.',
        'Revisa etiqueta o Vademécum antes de guardar dosis y cultivo.',
      ];
    }

    if (this.matchesFormContext(['fertilización', 'fertilizacion', 'fertilizante', 'abonado', 'abono'])) {
      return [
        'Revisa fecha, parcela, tipo de abono, cantidad y método de aplicación.',
        'Distingue si el formulario pide dosis por hectárea o cantidad total.',
        'Si hay fertirrigación, comprueba también los datos del riego.',
      ];
    }

    if (this.matchesFormContext(['maquinaria', 'máquina', 'maquina'])) {
      return [
        'Escribe un nombre claro para reconocer la máquina en las tareas.',
        'Revisa marca, modelo, matrícula o número ROMA si el formulario los pide.',
        'Comprueba que la máquina sea la que realmente se utiliza en campo.',
      ];
    }

    if (this.matchesFormContext(['trabajador', 'aplicador', 'personal'])) {
      return [
        'Comprueba nombre, NIF y datos de contacto de la persona.',
        'Si aplica fitosanitarios, revisa carné o número ROPO cuando corresponda.',
        'Guarda solo trabajadores reales de la explotación o empresa.',
      ];
    }

    if (this.matchesFormContext(['producto', 'productos', 'vademécum', 'vademecum'])) {
      return [
        'Revisa nombre comercial, tipo de producto y uso previsto.',
        'En fitosanitarios, contrasta cultivo, dosis y condiciones de uso con la información oficial.',
        'En fertilizantes, comprueba composición, unidad y forma de aplicación.',
      ];
    }

    return [
      'Revisa los campos obligatorios marcados con asterisco.',
      'Usa el icono azul de cada campo si necesitas saber qué significa o dónde encontrar el dato.',
      'Antes de guardar, comprueba que los datos correspondan a esta pantalla.',
    ];
  }

  public trackByGuidanceTip(index: number, tip: string): string {
    return `${index}-${tip}`;
  }

  private matchesFormContext(keywords: readonly string[]): boolean {
    const form = this.configuration.getForm();
    const formContext = this.normalizeText(`${form?.name ?? ''} ${form?.header ?? ''}`);

    return keywords.some((keyword) => formContext.includes(this.normalizeText(keyword)));
  }

  private normalizeText(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
